import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import routeMetadata from "./routeMetadata.json";
import {
  SITE_ORIGIN,
  SOCIAL_IMAGE,
  SOCIAL_IMAGE_ALT,
  SOCIAL_IMAGE_HEIGHT,
  SOCIAL_IMAGE_WIDTH,
} from "./metadataConfig";

export { SITE_ORIGIN } from "./metadataConfig";

function normalizePath(pathname) {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

function matchesRoute(pattern, pathname) {
  if (pattern === "*") return true;
  const patternParts = normalizePath(pattern).split("/").filter(Boolean);
  const pathParts = normalizePath(pathname).split("/").filter(Boolean);
  if (patternParts.length !== pathParts.length) return false;
  return patternParts.every((part, index) => part.startsWith(":") || part === pathParts[index]);
}

export function resolveRouteMetadata(pathname) {
  const normalized = normalizePath(pathname);
  return routeMetadata.find((entry) => entry.path !== "*" && matchesRoute(entry.path, normalized))
    || routeMetadata.find((entry) => entry.path === "*");
}

function setMeta(selector, attribute, value, content) {
  let element = document.head.querySelector(selector);
  if (!content) {
    element?.remove();
    return;
  }
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function setCanonical(path) {
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!path) {
    canonical?.remove();
    return;
  }
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", `${SITE_ORIGIN}${path}`);
}

function clearSocialMetadata() {
  [
    'meta[property="og:title"]',
    'meta[property="og:description"]',
    'meta[property="og:type"]',
    'meta[property="og:url"]',
    'meta[property="og:image"]',
    'meta[property="og:image:width"]',
    'meta[property="og:image:height"]',
    'meta[property="og:image:alt"]',
    'meta[property="og:site_name"]',
    'meta[name="twitter:card"]',
    'meta[name="twitter:title"]',
    'meta[name="twitter:description"]',
    'meta[name="twitter:image"]',
    'meta[name="twitter:image:alt"]',
  ].forEach((selector) => document.head.querySelector(selector)?.remove());
}

export function applyRouteMetadata(metadata) {
  document.title = metadata.title;
  setMeta('meta[name="description"]', "name", "description", metadata.description);
  setMeta('meta[name="robots"]', "name", "robots", metadata.robots);
  setCanonical(metadata.canonical);

  if (!metadata.social || !metadata.canonical) {
    clearSocialMetadata();
    return;
  }

  const canonicalUrl = `${SITE_ORIGIN}${metadata.canonical}`;
  setMeta('meta[property="og:title"]', "property", "og:title", metadata.title);
  setMeta('meta[property="og:description"]', "property", "og:description", metadata.description);
  setMeta('meta[property="og:type"]', "property", "og:type", "website");
  setMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);
  setMeta('meta[property="og:image"]', "property", "og:image", SOCIAL_IMAGE);
  setMeta('meta[property="og:image:width"]', "property", "og:image:width", SOCIAL_IMAGE_WIDTH);
  setMeta('meta[property="og:image:height"]', "property", "og:image:height", SOCIAL_IMAGE_HEIGHT);
  setMeta('meta[property="og:image:alt"]', "property", "og:image:alt", SOCIAL_IMAGE_ALT);
  setMeta('meta[property="og:site_name"]', "property", "og:site_name", "CleanIQ");
  setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary");
  setMeta('meta[name="twitter:title"]', "name", "twitter:title", metadata.title);
  setMeta('meta[name="twitter:description"]', "name", "twitter:description", metadata.description);
  setMeta('meta[name="twitter:image"]', "name", "twitter:image", SOCIAL_IMAGE);
  setMeta('meta[name="twitter:image:alt"]', "name", "twitter:image:alt", SOCIAL_IMAGE_ALT);
}

export default function RouteMetadata() {
  const location = useLocation();

  useEffect(() => {
    applyRouteMetadata(resolveRouteMetadata(location.pathname));
  }, [location.pathname]);

  return null;
}
